/**
 * WebSocket Service for AI Labs
 * Connects to Nova's AWS WebSocket API Gateway for streaming responses
 */

// AI Labs WebSocket API (us-east-1)
const WS_URL = import.meta.env.VITE_LABS_WS_URL || 'wss://uknjkyctd3.execute-api.us-east-1.amazonaws.com/prod';

/**
 * WebSocket connection manager for Labs
 */
class LabsWebSocket {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.callbacks = {
      onStart: null,
      onChunk: null,
      onAgentUpdate: null,
      onComplete: null,
      onAudioComplete: null,  // Audio (TTS/Podcast) 처리
      onError: null,
      onClose: null,
    };
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    return new Promise((resolve, reject) => {
      if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      try {
        this.ws = new WebSocket(WS_URL);

        this.ws.onopen = () => {
          console.log('[LabsWS] Connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onerror = (error) => {
          console.error('[LabsWS] Error:', error);
          if (this.callbacks.onError) {
            this.callbacks.onError({ type: 'connection', message: 'WebSocket connection error' });
          }
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log('[LabsWS] Closed:', event.code, event.reason);
          this.isConnected = false;
          if (this.callbacks.onClose) {
            this.callbacks.onClose(event);
          }
        };

        // Connection timeout
        setTimeout(() => {
          if (this.ws?.readyState !== WebSocket.OPEN) {
            this.ws?.close();
            reject(new Error('Connection timeout'));
          }
        }, 10000);

      } catch (error) {
        console.error('[LabsWS] Connection error:', error);
        reject(error);
      }
    });
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(data) {
    try {
      const message = JSON.parse(data);
      console.log('[LabsWS] Received:', message.type);

      switch (message.type) {
        case 'start':
          if (this.callbacks.onStart) this.callbacks.onStart(message);
          break;
        case 'chunk':
          if (this.callbacks.onChunk) this.callbacks.onChunk(message.text || message.content, message);
          break;
        case 'agent_update':
          if (this.callbacks.onAgentUpdate) this.callbacks.onAgentUpdate(message);
          break;
        case 'complete':
          if (this.callbacks.onComplete) this.callbacks.onComplete(message.result, message);
          break;
        case 'audio_complete':
          // TTS 또는 Podcast 오디오 생성 완료
          if (this.callbacks.onAudioComplete) this.callbacks.onAudioComplete(message);
          break;
        case 'error':
          if (this.callbacks.onError) this.callbacks.onError(message);
          break;
        default:
          console.log('[LabsWS] Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('[LabsWS] Message parse error:', error);
    }
  }

  /**
   * Send message to WebSocket
   */
  sendMessage(sourceText, options = {}) {
    if (!this.isConnected || this.ws?.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    const message = {
      action: 'sendMessage',
      sourceText,
      options: {
        mode: options.mode || 'auto',
        tool: options.tool || null, // table, document, search, voice
        ...options,
      },
    };

    console.log('[LabsWS] Sending:', message.action, 'tool:', options.tool);
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Set callback handlers
   */
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Disconnect
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
    }
  }

  get connected() {
    return this.isConnected && this.ws?.readyState === WebSocket.OPEN;
  }
}

// Singleton instance
const labsWS = new LabsWebSocket();

/**
 * Process with WebSocket streaming
 */
export const processWithStreaming = async (sourceText, options = {}, callbacks = {}) => {
  return new Promise(async (resolve, reject) => {
    let result = '';
    let isCompleted = false;

    try {
      labsWS.setCallbacks({
        onStart: (message) => {
          if (callbacks.onStart) callbacks.onStart(message);
        },
        onChunk: (text, message) => {
          result += text || '';
          if (callbacks.onChunk) callbacks.onChunk(text, result, message);
        },
        onAgentUpdate: (message) => {
          if (callbacks.onAgentUpdate) callbacks.onAgentUpdate(message);
        },
        onComplete: (finalResult, message) => {
          isCompleted = true;
          if (callbacks.onComplete) callbacks.onComplete(finalResult, message);
          resolve({ success: true, result: finalResult || result });
        },
        onAudioComplete: (message) => {
          // TTS/Podcast 오디오 완료
          if (callbacks.onAudioComplete) callbacks.onAudioComplete(message);
        },
        onError: (error) => {
          if (callbacks.onError) callbacks.onError(error);
          reject(new Error(error.message || 'Processing failed'));
        },
        onClose: (event) => {
          if (!isCompleted) {
            reject(new Error('Connection closed unexpectedly'));
          }
        },
      });

      if (!labsWS.connected) {
        await labsWS.connect();
      }

      labsWS.sendMessage(sourceText, options);

    } catch (error) {
      console.error('[LabsWS] Error:', error);
      reject(error);
    }
  });
};

export const disconnectLabs = () => labsWS.disconnect();
export const isLabsConnected = () => labsWS.connected;

export default { processWithStreaming, disconnectLabs, isLabsConnected };
